import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import { api } from "../../lib/axios";

type CatalogOption = {
  id: number;
  code: string;
  displayName: string;
  isActive: boolean;
  stock?: { calculated: number; reserved: number; available: number };
};

function apiErr(err: unknown, fallback: string): string {
  const ax = err as { response?: { data?: { error?: { message?: string } | string } } };
  const e = ax.response?.data?.error;
  if (typeof e === "string") return e;
  if (e && typeof e === "object" && "message" in e) return String(e.message);
  return fallback;
}

type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  initialCatalogId?: number | null;
};

const ResourceStockEntryModal: React.FC<Props> = ({ open, onClose, onSuccess, initialCatalogId = null }) => {
  const [catalog, setCatalog] = useState<CatalogOption[]>([]);
  const [resourceCatalogId, setResourceCatalogId] = useState<number | "">("");
  const [quantity, setQuantity] = useState("1");
  const [fournisseur, setFournisseur] = useState("");
  const [cout, setCout] = useState("");
  const [dateEntree, setDateEntree] = useState(() => new Date().toISOString().slice(0, 10));
  const [commentaire, setCommentaire] = useState("");
  const [loading, setLoading] = useState(false);

  const loadCatalog = useCallback(async () => {
    try {
      const res = await api.get("/resources/catalogue", { params: { actif: "true" } });
      const rows = (res.data?.data ?? []) as CatalogOption[];
      setCatalog(rows.filter((r) => r.isActive));
    } catch {
      setCatalog([]);
    }
  }, []);

  useEffect(() => {
    if (open) void loadCatalog();
  }, [open, loadCatalog]);

  useEffect(() => {
    if (open && initialCatalogId) {
      setResourceCatalogId(initialCatalogId);
    }
  }, [open, initialCatalogId]);

  if (!open) return null;

  const selectedCatalog = catalog.find((c) => c.id === resourceCatalogId) ?? null;
  const stockActuel = selectedCatalog?.stock?.calculated ?? 0;
  const qPreview = Number(quantity);
  const stockApres = Number.isInteger(qPreview) && qPreview > 0 ? stockActuel + qPreview : stockActuel;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const id = Number(resourceCatalogId);
    const q = Number(quantity);
    if (!id || !Number.isInteger(q) || q < 1) {
      toast.error("Sélectionnez un article catalogue existant.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/resources/stock/entries", {
        resourceCatalogId: id,
        quantity: q,
        fournisseur: fournisseur.trim() || null,
        cout: cout.trim() ? Number(cout) : null,
        dateEntree: dateEntree || null,
        commentaire: commentaire.trim() || null,
      });
      toast.success("Entrée stock enregistrée.");
      onSuccess();
      onClose();
      setQuantity("1");
      setFournisseur("");
      setCout("");
      setDateEntree(new Date().toISOString().slice(0, 10));
      setCommentaire("");
      setResourceCatalogId("");
    } catch (err) {
      toast.error(apiErr(err, "Entrée stock impossible."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-slate-700 bg-slate-900 p-5 text-slate-100 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Entrée stock (catalogue)</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-slate-400 hover:bg-slate-800 hover:text-white"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mb-4 text-sm text-slate-400">
          Choisissez un produit du catalogue existant. Les attributs (couleur, volume, etc.) sont sur la fiche
          catalogue uniquement.
        </p>
        <form onSubmit={(e) => void submit(e)} className="space-y-4">
          <label className="block text-xs text-slate-400">
            Article catalogue *
            <select
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
              value={resourceCatalogId}
              onChange={(e) => setResourceCatalogId(e.target.value ? Number(e.target.value) : "")}
              required
            >
              <option value="">— Sélectionner —</option>
              {catalog.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.code} — {c.displayName}
                </option>
              ))}
            </select>
            {catalog.length === 0 ? (
              <span className="mt-2 block rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-amber-100">
                Aucun article catalogue. <Link to="/admin/ressources/catalogue" className="underline">Créer article catalogue</Link>
              </span>
            ) : null}
          </label>
          <label className="block text-xs text-slate-400">
            Quantité
            <input
              type="number"
              min={1}
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
            />
          </label>
          <div className="rounded-xl border border-emerald-900/40 bg-emerald-950/20 px-3 py-2 text-sm text-emerald-100">
            Stock actuel : <span className="font-semibold">{stockActuel}</span>
            <span className="mx-2 text-emerald-500">→</span>
            Entrée : <span className="font-semibold">+{Number.isInteger(qPreview) && qPreview > 0 ? qPreview : 0}</span>
            <span className="mx-2 text-emerald-500">→</span>
            Stock après : <span className="font-semibold">{stockApres}</span>
          </div>
          <label className="block text-xs text-slate-400">
            Fournisseur
            <input
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
              value={fournisseur}
              onChange={(e) => setFournisseur(e.target.value)}
            />
          </label>
          <label className="block text-xs text-slate-400">
            Coût (optionnel)
            <input
              type="number"
              min={0}
              step="0.01"
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
              value={cout}
              onChange={(e) => setCout(e.target.value)}
            />
          </label>
          <label className="block text-xs text-slate-400">
            Date entrée
            <input
              type="date"
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
              value={dateEntree}
              onChange={(e) => setDateEntree(e.target.value)}
            />
          </label>
          <label className="block text-xs text-slate-400">
            Commentaire
            <textarea
              className="mt-1 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white"
              rows={2}
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
            />
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-slate-600 px-4 py-2 text-sm text-slate-300 hover:bg-slate-800"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50"
            >
              {loading ? "…" : "Valider l’entrée"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResourceStockEntryModal;
